import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Form, Button, Dropdown, Spinner } from "react-bootstrap";
import { supabase } from "../supabaseClient";
import moment from "moment-timezone";
import MicButton from "./common/MicButton";
import MultiSelectFilterBar from "./common/MultiSelectFilterBar";
import TaskAssistantChat from "./common/TaskAssistantChat";
import ChatAssistantInput from "./common/ChatAssistantInput";

// Project theme aligned styles - matching other modals
const styles = {
  header: (isDarkMode) => ({
    background: "#000",
    borderBottom: "none",
    padding: "16px 20px",
    color: "#fff",
  }),
  headerTitle: (isDarkMode) => ({
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  }),
  body: (isDarkMode) => ({
    background: isDarkMode ? "#1a1a2e" : "#ffffff",
    padding: "20px",
    minHeight: "500px",
    color: isDarkMode ? "#e4e4e4" : "#333333",
  }),
  searchInput: (isDarkMode) => ({
    backgroundColor: isDarkMode ? "#252542" : "#f8f9fa",
    border: `1px solid ${isDarkMode ? "#3d3d5c" : "#e9ecef"}`,
    borderRadius: "10px",
    padding: "12px 16px",
    color: isDarkMode ? "#e4e4e4" : "#333333",
    fontSize: "14px",
    transition: "all 0.2s ease",
  }),
  filterContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignItems: "center",
    marginBottom: "20px",
  },
  filterButton: (isActive, variant, isDarkMode) => {
    const colors = {
      primary: "#009689",
      info: "#17a2b8",
      warning: "#ffc107",
      success: "#009689",
      secondary: "#6c757d",
      danger: "#dc3545",
    };
    const color = colors[variant] || colors.primary;
    
    return {
      padding: "8px 16px",
      borderRadius: "20px",
      fontSize: "13px",
      fontWeight: 500,
      border: isActive ? "none" : `1px solid ${isDarkMode ? "#3d3d5c" : "#e9ecef"}`,
      background: isActive ? color : (isDarkMode ? "#252542" : "#ffffff"),
      color: isActive ? "#FFFFFF" : (isDarkMode ? "#e4e4e4" : "#333333"),
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    };
  },
  filterCount: (isActive, isDarkMode) => ({
    backgroundColor: isActive ? "rgba(255,255,255,0.25)" : (isDarkMode ? "#3d3d5c" : "#e9ecef"),
    color: isActive ? "#FFFFFF" : (isDarkMode ? "#e4e4e4" : "#333333"),
    padding: "2px 8px",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: 600,
  }),
  divider: (isDarkMode) => ({
    width: "1px",
    height: "32px",
    backgroundColor: isDarkMode ? "#3d3d5c" : "#e9ecef",
    margin: "0 8px",
  }),
  card: (isDarkMode, statusColor) => ({
    background: isDarkMode ? "#252542" : "#f8f9fa",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "12px",
    borderLeft: `4px solid ${statusColor}`,
    border: `1px solid ${isDarkMode ? "#3d3d5c" : "#e9ecef"}`,
    transition: "all 0.2s ease",
    cursor: "default",
  }),
  cardTitle: (isDarkMode) => ({
    margin: 0,
    fontWeight: 600,
    color: isDarkMode ? "#e4e4e4" : "#333333",
    fontSize: "15px",
    lineHeight: "1.4",
  }),
  cardDescription: (isDarkMode) => ({
    margin: "8px 0 0 0",
    fontSize: "13px",
    color: isDarkMode ? "#9ca3af" : "#6c757d",
    lineHeight: "1.5",
  }),
  badge: (variant, isDarkMode) => {
    const variants = {
      info: { 
        bg: isDarkMode ? "rgba(0,150,137,0.2)" : "rgba(0,150,137,0.1)", 
        color: "#009689",
      },
      warning: { 
        bg: isDarkMode ? "rgba(255,193,7,0.2)" : "rgba(255,193,7,0.15)", 
        color: isDarkMode ? "#ffc107" : "#856404",
      },
      success: { 
        bg: isDarkMode ? "rgba(0,150,137,0.2)" : "rgba(0,150,137,0.1)", 
        color: "#009689",
      },
      secondary: { 
        bg: isDarkMode ? "rgba(108,117,125,0.3)" : "rgba(108,117,125,0.15)", 
        color: isDarkMode ? "#adb5bd" : "#6c757d",
      },
      danger: { 
        bg: isDarkMode ? "rgba(220,53,69,0.2)" : "rgba(220,53,69,0.1)", 
        color: "#dc3545",
      },
    };
    const v = variants[variant] || variants.secondary;
    return {
      padding: "4px 10px",
      borderRadius: "6px",
      fontSize: "11px",
      fontWeight: 600,
      backgroundColor: v.bg,
      color: v.color,
      border: "none",
      textTransform: "capitalize",
    };
  },
  metaText: (isDarkMode, isHighlight) => ({
    fontSize: "12px",
    fontWeight: 500,
    color: isHighlight 
      ? "#009689"
      : (isDarkMode ? "#9ca3af" : "#6c757d"),
    display: "flex",
    alignItems: "center",
    gap: "5px",
  }),
  metaIcon: {
    width: "14px",
    height: "14px",
  },
  menuButton: (isDarkMode) => ({
    background: isDarkMode ? "#252542" : "#ffffff",
    border: `1px solid ${isDarkMode ? "#3d3d5c" : "#e9ecef"}`,
    borderRadius: "8px",
    padding: "6px 10px",
    color: isDarkMode ? "#e4e4e4" : "#6c757d",
    cursor: "pointer",
    transition: "all 0.2s ease",
  }),
  editPanel: (isDarkMode) => ({
    width: "380px",
    background: isDarkMode ? "#252542" : "#ffffff",
    borderRadius: "12px",
    padding: "20px",
    borderLeft: `1px solid ${isDarkMode ? "#3d3d5c" : "#e9ecef"}`,
  }),
  editPanelTitle: (isDarkMode) => ({
    margin: 0,
    fontWeight: 600,
    fontSize: "18px",
    color: isDarkMode ? "#e4e4e4" : "#333333",
  }),
  formLabel: (isDarkMode) => ({
    fontWeight: 500,
    fontSize: "13px",
    color: isDarkMode ? "#9ca3af" : "#6c757d",
    marginBottom: "6px",
  }),
  formInput: (isDarkMode) => ({
    backgroundColor: isDarkMode ? "#1a1a2e" : "#f8f9fa",
    border: `1px solid ${isDarkMode ? "#3d3d5c" : "#e9ecef"}`,
    borderRadius: "8px",
    color: isDarkMode ? "#e4e4e4" : "#333333",
    padding: "10px 14px",
    fontSize: "14px",
  }),
  primaryButton: {
    background: "#009689",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    fontWeight: 600,
    fontSize: "14px",
    color: "#ffffff",
  },
  emptyState: (isDarkMode) => ({
    textAlign: "center",
    padding: "60px 20px",
    color: isDarkMode ? "#9ca3af" : "#6c757d",
  }),
  emptyIcon: {
    width: "64px",
    height: "64px",
    marginBottom: "16px",
    opacity: 0.5,
  },
};

// SVG Icons - Project theme colors
const CalendarIcon = ({ color }) => (
  <svg style={styles.metaIcon} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const RepeatIcon = ({ color }) => (
  <svg style={styles.metaIcon} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9"/>
    <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
    <polyline points="7 23 3 19 7 15"/>
    <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
  </svg>
);

const BellIcon = () => (
  <svg style={{ width: "20px", height: "20px", display: "inline-block" }} viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

const TrashIcon = () => (
  <svg style={{ width: "14px", height: "14px", marginRight: "4px" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const EmptyIcon = ({ isDarkMode }) => (
  <svg style={styles.emptyIcon} viewBox="0 0 24 24" fill="none" stroke={isDarkMode ? "#9ca3af" : "#6c757d"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    <line x1="2" y1="2" x2="22" y2="22"/>
  </svg>
);

const ReminderModal = ({ show, onHide, isDarkMode, userId }) => {
  const [reminders, setReminders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [userTimeZone, setUserTimeZone] = useState(null);
  
  // Filter states (multi-select: Set of selected values, empty set = show all)
  const [typeFilters, setTypeFilters] = useState(new Set()); // empty = all types
  const [statusFilters, setStatusFilters] = useState(new Set(["upcoming", "missed"])); // default: hide completed
  
  // CRUD states
  const [editingReminder, setEditingReminder] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedReminders, setSelectedReminders] = useState([]);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  
  // Edit form states
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [saving, setSaving] = useState(false);

  // Chat assistant states
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatWidthPercent, setChatWidthPercent] = useState(70);
  const [isResizing, setIsResizing] = useState(false);
  const chatAssistantRef = useRef(null);
  const modalBodyRef = useRef(null);

  // Resize handler for chat width
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !modalBodyRef.current) return;

    const modalBody = modalBodyRef.current;
    const modalRect = modalBody.getBoundingClientRect();
    const mouseX = e.clientX - modalRect.left;
    const modalWidth = modalRect.width;

    const chatWidth = modalWidth - mouseX;
    let newPercent = (chatWidth / modalWidth) * 100;

    newPercent = Math.max(20, Math.min(70, newPercent));
    setChatWidthPercent(newPercent);
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Fetch reminders from Supabase
  const fetchReminders = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("reminders")
        .select("*")
        .eq("user_id", userId)
        .order("reminder_date", { ascending: true });

      if (error) {
        console.error("Error fetching reminders:", error);
      } else {
        setReminders(data || []);
      }
    } catch (error) {
      console.error("Error fetching reminders:", error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Fetch user timezone
  const fetchUserTimezone = async () => {
    if (!userId) return;

    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("time_zone")
        .eq("id", userId)
        .single();

      setUserTimeZone(profile?.time_zone || "Asia/Kolkata");
    } catch (error) {
      console.error("Error fetching user timezone:", error);
      setUserTimeZone("Asia/Kolkata");
    }
  };

  // Subscribe to realtime updates
  useEffect(() => {
    if (!show || !userId) return;

    const channel = supabase
      .channel(`reminders_${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "reminders",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          console.log("Reminder change:", payload);
          if (payload.eventType === "INSERT") {
            setReminders((prev) => [...prev, payload.new].sort((a, b) => 
              new Date(a.reminder_date) - new Date(b.reminder_date)
            ));
          } else if (payload.eventType === "UPDATE") {
            setReminders((prev) =>
              prev.map((r) => (r.id === payload.new.id ? payload.new : r))
            );
          } else if (payload.eventType === "DELETE") {
            setReminders((prev) => prev.filter((r) => r.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [show, userId]);

  // Determine reminder type (simple, complex, or ai)
  const getReminderType = (reminder) => {
    if (reminder.type === "ai_query") return "ai";
    if (reminder.type) return reminder.type;
    if (reminder.actions && reminder.actions.length > 0) return "complex";
    if (reminder.is_notification === false) return "complex";
    return "simple";
  };

  // Determine reminder status
  const getReminderStatus = (reminder) => {
    // Normalize database status values to our expected values
    if (reminder.status) {
      const normalizedStatus = reminder.status.toLowerCase();
      // Map "active" to "upcoming" for sorting/filtering
      if (normalizedStatus === "active") return "upcoming";
      if (normalizedStatus === "completed" || normalizedStatus === "missed" || normalizedStatus === "upcoming") {
        return normalizedStatus;
      }
    }
    
    if (reminder.send_alert === false) return "completed";

    const timeToUse = userTimeZone || "Asia/Kolkata";
    const now = moment().tz(timeToUse);
    const reminderDate = moment.utc(reminder.reminder_date).tz(timeToUse);
    
    if (reminderDate.isBefore(now)) {
      return reminder.send_alert ? "missed" : "completed";
    }
    return "upcoming";
  };

  const actionableReminders = reminders.filter(
    (reminder) => getReminderStatus(reminder) !== "completed"
  );

  // Filter reminders
  const filteredReminders = actionableReminders
    .filter((reminder) => {
      // Search filter
      const matchesSearch =
        reminder.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reminder.description?.toLowerCase().includes(searchTerm.toLowerCase());

      // Type filter (multi-select: empty set = all)
      const reminderType = getReminderType(reminder);
      const matchesType = typeFilters.size === 0 || typeFilters.has(reminderType);

      // Status filter (multi-select: empty set = all)
      const reminderStatus = getReminderStatus(reminder);
      const matchesStatus = statusFilters.size === 0 || statusFilters.has(reminderStatus);

      return matchesSearch && matchesType && matchesStatus;
    })
    .sort((a, b) => {
      const statusA = getReminderStatus(a);
      const statusB = getReminderStatus(b);
      
      // Priority order: missed first, then upcoming
      const priorityOrder = { missed: 0, upcoming: 1 };
      const priorityDiff = priorityOrder[statusA] - priorityOrder[statusB];
      
      if (priorityDiff !== 0) return priorityDiff;
      
      // Within same status, sort by date
      if (statusA === 'missed') {
        // Oldest missed first
        return new Date(a.reminder_date) - new Date(b.reminder_date);
      }

      // Upcoming: soonest first
      return new Date(a.reminder_date) - new Date(b.reminder_date);
    });

  // Truncate text
  const truncateText = (text, reminderId) => {
    if (!text) return "No description";
    const words = text.split(" ");
    const isExpanded = expandedItems[reminderId];
    const maxWords = 20;

    if (words.length <= maxWords || isExpanded) {
      return text;
    }
    return words.slice(0, maxWords).join(" ") + "...";
  };

  const toggleExpanded = (reminderId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [reminderId]: !prev[reminderId],
    }));
  };

  // Date display helper
  const getReminderDateDisplay = (dateString, reminder) => {
    if (!dateString) return { display: "", tooltip: "", status: "unknown" };

    const timeToUse = userTimeZone || "Asia/Kolkata";
    const now = moment().tz(timeToUse);
    const reminderDate = moment.utc(dateString).tz(timeToUse);
    const today = moment().tz(timeToUse).startOf("day");
    const reminderDay = moment.utc(dateString).tz(timeToUse).startOf("day");
    const fullDateTime = reminderDate.format("dddd, MMMM D, YYYY [at] h:mm A");

    let display = "";
    let status = "upcoming";

    if (reminderDay.isSame(today, "day")) {
      display = `Today ${reminderDate.format("h:mm A")}`;
    } else if (reminderDay.isSame(today.clone().add(1, "day"), "day")) {
      display = `Tomorrow ${reminderDate.format("h:mm A")}`;
    } else if (reminderDate.isAfter(now)) {
      display = reminderDate.format("MMM D, h:mm A");
    } else {
      display = reminderDate.format("MMM D, h:mm A");
      status = "past";
    }

    // Add execution info if available
    let executionInfo = "";
    if (reminder.last_executed_at) {
      const lastExec = moment(reminder.last_executed_at).tz(timeToUse);
      executionInfo = `\nLast triggered: ${lastExec.format("MMM D, h:mm A")}`;
    }
    if (reminder.execution_count > 0) {
      executionInfo += `\nTriggered ${reminder.execution_count} time${reminder.execution_count > 1 ? "s" : ""}`;
    }

    return {
      display,
      tooltip: fullDateTime + executionInfo,
      status,
    };
  };

  // Card background color
  const getCardBackgroundColor = (reminder) => {
    const status = getReminderStatus(reminder);
    
    if (status === "completed") {
      return isDarkMode ? "#1A4A1A" : "#DCFCE7"; // Green
    } else if (status === "missed") {
      return isDarkMode ? "#4A1A1A" : "#FEE2E2"; // Red
    } else if (reminder.is_repeat) {
      return isDarkMode ? "#1A3A4A" : "#DBEAFE"; // Blue for recurring
    }
    return isDarkMode ? "#2D2D2D" : "#fff"; // Default
  };

  // CRUD Operations
  const handleEdit = (reminder) => {
    setEditingReminder(reminder);
    setEditTitle(reminder.title || "");
    setEditDescription(reminder.description || "");
    
    const reminderMoment = moment(reminder.reminder_date).tz(userTimeZone || "Asia/Kolkata");
    setEditDate(reminderMoment.format("YYYY-MM-DD"));
    setEditTime(reminderMoment.format("HH:mm"));
    
    setShowEditPanel(true);
  };

  const handleSaveEdit = async () => {
    if (!editingReminder) return;
    
    setSaving(true);
    try {
      const newDateTime = moment.tz(
        `${editDate} ${editTime}`,
        "YYYY-MM-DD HH:mm",
        userTimeZone || "Asia/Kolkata"
      ).toISOString();

      const { error } = await supabase
        .from("reminders")
        .update({
          title: editTitle,
          description: editDescription,
          reminder_date: newDateTime,
        })
        .eq("id", editingReminder.id);

      if (error) throw error;

      setShowEditPanel(false);
      setEditingReminder(null);
      // Realtime will handle the update
    } catch (error) {
      console.error("Error updating reminder:", error);
      alert("Failed to update reminder");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reminderId) => {
    try {
      const { error } = await supabase
        .from("reminders")
        .delete()
        .eq("id", reminderId);

      if (error) throw error;
      setDeleteConfirm(null);
      await fetchReminders(); // Refresh list after delete
    } catch (error) {
      console.error("Error deleting reminder:", error);
      alert("Failed to delete reminder");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedReminders.length === 0) return;

    try {
      const { error } = await supabase
        .from("reminders")
        .delete()
        .in("id", selectedReminders);

      if (error) throw error;
      
      setSelectedReminders([]);
      setBulkDeleteMode(false);
      await fetchReminders(); // Refresh list after bulk delete
    } catch (error) {
      console.error("Error bulk deleting reminders:", error);
      alert("Failed to delete reminders");
    }
  };

  const handleMarkComplete = async (reminder) => {
    try {
      const { error } = await supabase
        .from("reminders")
        .update({
          send_alert: false,
          status: "completed",
          completed_at: new Date().toISOString(),
        })
        .eq("id", reminder.id);

      if (error) throw error;
    } catch (error) {
      console.error("Error marking reminder complete:", error);
    }
  };

  const handleToggleSelect = (reminderId) => {
    setSelectedReminders((prev) =>
      prev.includes(reminderId)
        ? prev.filter((id) => id !== reminderId)
        : [...prev, reminderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedReminders.length === filteredReminders.length) {
      setSelectedReminders([]);
    } else {
      setSelectedReminders(filteredReminders.map((r) => r.id));
    }
  };

  // Fetch data when modal opens
  useEffect(() => {
    if (show && userId) {
      fetchUserTimezone();
      fetchReminders();
    }
  }, [show, userId, fetchReminders]);

  const handleClose = () => {
    setSearchTerm("");
    setShowEditPanel(false);
    setEditingReminder(null);
    setBulkDeleteMode(false);
    setSelectedReminders([]);
    setTypeFilters(new Set());
    setStatusFilters(new Set(["upcoming", "missed"]));
    onHide();
  };

  // Count by status for filter badges
  const statusCounts = {
    upcoming: actionableReminders.filter((r) => getReminderStatus(r) === "upcoming").length,
    missed: actionableReminders.filter((r) => getReminderStatus(r) === "missed").length,
  };

  const typeCounts = {
    simple: actionableReminders.filter((r) => getReminderType(r) === "simple").length,
    complex: actionableReminders.filter((r) => getReminderType(r) === "complex").length,
    ai: actionableReminders.filter((r) => getReminderType(r) === "ai").length,
  };

  // Get status color for card border - project theme colors
  const getStatusColor = (status) => {
    switch (status) {
      case "upcoming": return "#009689"; // Teal (project primary)
      case "completed": return "#6c757d"; // Gray
      case "missed": return "#dc3545"; // Red
      default: return "#6c757d";
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" className="task-popup export-popup" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <BellIcon />
            All Reminders
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        ref={modalBodyRef}
        className="export-more"
        style={{
          overflow: "visible",
          display: "flex",
          flexDirection: "row",
          height: "70vh",
          padding: 0,
        }}
      >
        {/* Resize Handle */}
        {!isChatMinimized && (
          <div
            onMouseDown={handleResizeStart}
            style={{
              width: '14px',
              height: '100%',
              background: 'var(--border-color, #d5e5e3)',
              cursor: 'col-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              order: 1,
              position: 'relative',
              zIndex: 10,
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--teal-bg, #b8d4d0)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--border-color, #d5e5e3)'}
          >
            <i className="bi bi-grip-vertical" style={{ fontSize: '1rem', color: 'var(--text, #666)', opacity: 0.6 }}></i>
          </div>
        )}

        {/* Chat Assistant Panel - RIGHT SIDE */}
        <div style={{
          width: isChatMinimized ? '0px' : `${chatWidthPercent}%`,
          minWidth: isChatMinimized ? '0px' : '280px',
          maxWidth: isChatMinimized ? '0px' : '70%',
          height: '100%',
          transition: isResizing ? 'none' : 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          borderLeft: isChatMinimized ? 'none' : 'none',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          position: 'relative',
          background: isChatMinimized ? 'transparent' : '#FBF9FA',
          overflow: 'visible',
          order: 2
        }}>
          <TaskAssistantChat
            ref={chatAssistantRef}
            isDarkMode={isDarkMode}
            onToggle={(minimized) => setIsChatMinimized(minimized)}
            showInput={false}
            externalInput={chatInput}
            onExternalInputChange={setChatInput}
            contextType="reminders"
            userId={userId}
            onItemsUpdated={() => {
              fetchReminders();
            }}
          />
        </div>

        {/* Main Content - LEFT SIDE */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: '16px',
          order: 0
        }}>
          {/* Search Input and Bulk Delete */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "12px", alignItems: "center" }}>
            <Form.Control
              type="text"
              placeholder="Search title or description"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...styles.searchInput(isDarkMode), flex: 1 }}
            />
            {bulkDeleteMode ? (
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  style={{
                    padding: "8px 14px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 500,
                    border: `1px solid ${isDarkMode ? "#3d3d5c" : "#e9ecef"}`,
                    background: isDarkMode ? "#252542" : "#ffffff",
                    color: isDarkMode ? "#e4e4e4" : "#333333",
                    cursor: "pointer",
                  }}
                  onClick={handleSelectAll}
                >
                  {selectedReminders.length === filteredReminders.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
                <button
                  style={{
                    padding: "8px 14px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 500,
                    border: "none",
                    background: "#dc3545",
                    color: "#ffffff",
                    cursor: "pointer",
                  }}
                  onClick={handleBulkDelete}
                  disabled={selectedReminders.length === 0}
                >
                  Delete ({selectedReminders.length})
                </button>
                <button
                  style={{
                    padding: "8px 14px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 500,
                    border: `1px solid ${isDarkMode ? "#3d3d5c" : "#e9ecef"}`,
                    background: isDarkMode ? "#252542" : "#ffffff",
                    color: isDarkMode ? "#e4e4e4" : "#333333",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setBulkDeleteMode(false);
                    setSelectedReminders([]);
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                style={{
                  padding: "12px 16px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 500,
                  border: "none",
                  background: isDarkMode ? "rgba(220,53,69,0.15)" : "rgba(220,53,69,0.1)",
                  color: "#dc3545",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  whiteSpace: "nowrap",
                }}
                onClick={() => setBulkDeleteMode(true)}
              >
                <TrashIcon />
                Bulk Delete
              </button>
            )}
          </div>

          {/* Filter Buttons (Multi-Select) */}
          <MultiSelectFilterBar
            isDarkMode={isDarkMode}
            filterGroups={[
              {
                key: "type",
                label: "Types",
                options: [
                  { value: "simple", label: "Simple", variant: "info", count: typeCounts.simple },
                  { value: "complex", label: "Complex", variant: "warning", count: typeCounts.complex },
                  { value: "ai", label: "Zoomey", variant: "purple", count: typeCounts.ai },
                ],
              },
              {
                key: "status",
                label: "Status",
                options: [
                  { value: "missed", label: "Missed", variant: "danger", count: statusCounts.missed },
                  { value: "upcoming", label: "Upcoming", variant: "success", count: statusCounts.upcoming },
                ],
              },
            ]}
            selectedFilters={{
              type: typeFilters,
              status: statusFilters,
            }}
            onFilterChange={(groupKey, newSet) => {
              if (groupKey === "type") setTypeFilters(newSet);
              else if (groupKey === "status") setStatusFilters(newSet);
            }}
          />

          {/* Reminders List */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              paddingRight: "8px",
              minHeight: 0,
            }}
          >
            {loading ? (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <Spinner animation="border" role="status" style={{ color: isDarkMode ? "#3B82F6" : "#2563EB" }}>
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : filteredReminders.length > 0 ? (
              filteredReminders.map((reminder) => {
                const dateInfo = getReminderDateDisplay(reminder.reminder_date, reminder);
                const reminderType = getReminderType(reminder);
                const reminderStatus = getReminderStatus(reminder);
                const statusColor = getStatusColor(reminderStatus);

                return (
                  <div
                    key={reminder.id}
                    style={styles.card(isDarkMode, statusColor)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = isDarkMode 
                        ? "0 4px 12px rgba(0,0,0,0.3)"
                        : "0 4px 12px rgba(0,0,0,0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                      {/* Checkbox for bulk delete */}
                      {bulkDeleteMode && (
                        <div style={{ paddingTop: "2px" }}>
                          <input
                            type="checkbox"
                            style={{ 
                              width: "18px", 
                              height: "18px", 
                              cursor: "pointer",
                              accentColor: "#3B82F6",
                            }}
                            checked={selectedReminders.includes(reminder.id)}
                            onChange={() => handleToggleSelect(reminder.id)}
                          />
                        </div>
                      )}

                      {/* Content */}
                      <div style={{ flex: 1 }}>
                        {/* Title and Badges Row */}
                        <div style={{ 
                          display: "flex", 
                          alignItems: "flex-start", 
                          justifyContent: "space-between",
                          gap: "12px",
                        }}>
                          <h5 style={styles.cardTitle(isDarkMode)}>
                            {reminder.title || "Untitled Reminder"}
                          </h5>
                          <div style={{ display: "flex", gap: "6px", flexShrink: 0, alignItems: "center" }}>
                            <span style={styles.badge(reminderType === "simple" ? "info" : "warning", isDarkMode)}>
                              {reminderType === "simple" ? "Simple Reminder" : "Complex"}
                            </span>
                            <span style={styles.badge(
                              reminderStatus === "upcoming" ? "success" 
                                : reminderStatus === "completed" ? "secondary" 
                                : "danger",
                              isDarkMode
                            )}>
                              {reminderStatus === "upcoming" ? "Active" : reminderStatus.charAt(0).toUpperCase() + reminderStatus.slice(1)}
                            </span>
                            {/* Action Menu - Top Right */}
                            {!bulkDeleteMode && (
                              <Dropdown>
                                <Dropdown.Toggle
                                  as="button"
                                  bsPrefix="custom-dropdown-toggle"
                                  style={{
                                    ...styles.menuButton(isDarkMode),
                                    marginLeft: "4px",
                                  }}
                                >
                                  <i className="bi bi-three-dots-vertical"></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu 
                                  style={{ 
                                    background: isDarkMode ? "#252542" : "#ffffff",
                                    border: `1px solid ${isDarkMode ? "#3d3d5c" : "#e9ecef"}`,
                                    borderRadius: "8px",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    padding: "4px",
                                  }}
                                >
                                  <Dropdown.Item 
                                    onClick={() => handleEdit(reminder)}
                                    style={{ color: isDarkMode ? "#F1F5F9" : "#1E293B" }}
                                  >
                                    <i className="bi bi-pencil me-2"></i>Edit
                                  </Dropdown.Item>
                                  {reminderStatus === "upcoming" && (
                                    <Dropdown.Item 
                                      onClick={() => handleMarkComplete(reminder)}
                                      style={{ color: isDarkMode ? "#F1F5F9" : "#1E293B" }}
                                    >
                                      <i className="bi bi-check-circle me-2"></i>Mark Complete
                                    </Dropdown.Item>
                                  )}
                                  <Dropdown.Divider style={{ borderColor: isDarkMode ? "#334155" : "#E2E8F0" }} />
                                  <Dropdown.Item
                                    onClick={() => setDeleteConfirm(reminder.id)}
                                    style={{ color: "#EF4444" }}
                                  >
                                    <i className="bi bi-trash me-2"></i>Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </div>
                        </div>

                        {/* Description */}
                        <p style={styles.cardDescription(isDarkMode)}>
                          {truncateText(reminder.description, reminder.id)}
                          {reminder.description?.split(" ").length > 20 && (
                            <button
                              onClick={() => toggleExpanded(reminder.id)}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#3B82F6",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontWeight: 600,
                                padding: "0 4px",
                              }}
                            >
                              {expandedItems[reminder.id] ? "less" : "more"}
                            </button>
                          )}
                        </p>

                        {/* Date and Meta Info */}
                        <div style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between",
                          marginTop: "12px",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                            <span
                              style={styles.metaText(isDarkMode, dateInfo.status !== "past")}
                              title={dateInfo.tooltip}
                            >
                              <CalendarIcon color={dateInfo.status === "past" 
                                ? (isDarkMode ? "#EF4444" : "#DC2626")
                                : (isDarkMode ? "#4ADE80" : "#16A34A")} 
                              />
                              {dateInfo.display}
                            </span>
                            
                            {reminder.is_repeat && (
                              <span style={{ ...styles.metaText(isDarkMode, false), color: "#3B82F6" }}>
                                <RepeatIcon color="#3B82F6" />
                                Recurring
                              </span>
                            )}

                            {reminder.execution_count > 0 && (
                              <span style={{ ...styles.metaText(isDarkMode, false), fontSize: "11px" }}>
                                Triggered {reminder.execution_count}x
                              </span>
                            )}
                          </div>

                        </div>

                        {/* Delete Confirmation */}
                        {deleteConfirm === reminder.id && (
                          <div
                            style={{
                              marginTop: "12px",
                              padding: "10px 14px",
                              borderRadius: "8px",
                              background: isDarkMode ? "rgba(220,53,69,0.15)" : "rgba(220,53,69,0.1)",
                              border: `1px solid ${isDarkMode ? "rgba(220,53,69,0.3)" : "rgba(220,53,69,0.2)"}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span style={{ fontSize: "13px", fontWeight: 500, color: "#dc3545" }}>
                              Delete this reminder?
                            </span>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <Button
                                size="sm"
                                style={{
                                  background: "#dc3545",
                                  border: "none",
                                  borderRadius: "6px",
                                  fontWeight: 500,
                                  padding: "4px 12px",
                                }}
                                onClick={() => handleDelete(reminder.id)}
                              >
                                Yes
                              </Button>
                              <Button
                                size="sm"
                                style={{ 
                                  borderRadius: "6px",
                                  background: isDarkMode ? "#252542" : "#ffffff",
                                  border: `1px solid ${isDarkMode ? "#3d3d5c" : "#e9ecef"}`,
                                  color: isDarkMode ? "#e4e4e4" : "#333333",
                                  fontWeight: 500,
                                  padding: "4px 12px",
                                }}
                                onClick={() => setDeleteConfirm(null)}
                              >
                                No
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={styles.emptyState(isDarkMode)}>
                <EmptyIcon isDarkMode={isDarkMode} />
                <p style={{ margin: 0, fontSize: "15px", fontWeight: 500 }}>
                  {searchTerm || typeFilters.size > 0 || statusFilters.size > 0
                    ? "No reminders found matching your filters."
                    : "No reminders found. Create one by asking Zoomey!"}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Edit Panel (Slide-out) */}
        {showEditPanel && (
          <div style={{ ...styles.editPanel(isDarkMode), order: 0, flexShrink: 0, overflowY: 'auto', height: '100%' }}>
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "space-between",
              marginBottom: "24px",
            }}>
              <h5 style={styles.editPanelTitle(isDarkMode)}>Edit Reminder</h5>
              <button
                onClick={() => {
                  setShowEditPanel(false);
                  setEditingReminder(null);
                }}
                style={{
                  background: isDarkMode ? "#334155" : "#F1F5F9",
                  border: "none",
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  fontSize: "18px",
                  cursor: "pointer",
                  color: isDarkMode ? "#94A3B8" : "#64748B",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>

            <Form>
              <Form.Group style={{ marginBottom: "20px" }}>
                <Form.Label style={styles.formLabel(isDarkMode)}>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  style={styles.formInput(isDarkMode)}
                />
              </Form.Group>

              <Form.Group style={{ marginBottom: "20px" }}>
                <Form.Label style={styles.formLabel(isDarkMode)}>Description</Form.Label>
                <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    style={{ ...styles.formInput(isDarkMode), flex: 1 }}
                  />
                  <MicButton
                    onTranscript={(text) => setEditDescription(prev => prev ? `${prev} ${text}` : text)}
                    size="sm"
                  />
                </div>
              </Form.Group>

              <Form.Group style={{ marginBottom: "20px" }}>
                <Form.Label style={styles.formLabel(isDarkMode)}>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  style={styles.formInput(isDarkMode)}
                />
              </Form.Group>

              <Form.Group style={{ marginBottom: "24px" }}>
                <Form.Label style={styles.formLabel(isDarkMode)}>Time</Form.Label>
                <Form.Control
                  type="time"
                  value={editTime}
                  onChange={(e) => setEditTime(e.target.value)}
                  style={styles.formInput(isDarkMode)}
                />
              </Form.Group>

              <div style={{ display: "flex", gap: "12px" }}>
                <Button
                  onClick={handleSaveEdit}
                  disabled={saving || !editTitle}
                  style={{ ...styles.primaryButton, flex: 1 }}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" style={{ marginRight: "8px" }} />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setShowEditPanel(false);
                    setEditingReminder(null);
                  }}
                  style={{ borderRadius: "10px", fontWeight: 500 }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px' }}>
        <ChatAssistantInput
          chatAssistantRef={chatAssistantRef}
          inputValue={chatInput}
          onInputChange={setChatInput}
          onClearChat={() => chatAssistantRef.current?.clearChat()}
          autoFocus={true}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ReminderModal;
